"use client";

import { useEffect, useState } from "react";

const SEEN_KEY = "gigs_console_intro_seen_v1";

const TERMS = [
    { term: "Gig", desc: "A short, tactical piece of volunteer work — one exam to curate, one reference to sponsor. Not a standing committee seat." },
    { term: "Posting", desc: "An open gig volunteers can apply to. It stays invisible until it is approved." },
    { term: "Eligibility gate", desc: "The rules (geography, membership, prior roles) that decide who ever sees a posting. It filters for you." },
    { term: "Standing pool", desc: "A bench of volunteers who raised their hand in advance, so recurring gigs don't need a fresh posting." },
];

const STAFF_STEPS = [
    "Review what groups submit — approve, edit before approving, or send back with a note.",
    "Approving publishes the gig to every eligible volunteer at once.",
    "Triage who applied or was invited, then record the assignment.",
    "Close it out when the work is done — that is what the volunteer's record shows.",
];

const LEADER_STEPS = [
    "Create a posting for the work your group needs done.",
    "Staff review it before it goes live — you'll see it move out of the queue.",
    "Track who applies, invite people directly, and confirm your pick.",
    "Mark it complete when the work is finished.",
];

/**
 * First-visit explainer for the Gigs console — the same pattern as the policy
 * workspace's OrientationNote: shown once, then reachable from "What is this?".
 * "Gig" is the one bit of jargon on this surface that nothing else explains.
 */
export function ConsoleIntroNote({ isStaffView }: { isStaffView: boolean }) {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (typeof window !== "undefined" && !localStorage.getItem(SEEN_KEY)) {
            setOpen(true);
        }
    }, []);

    const close = () => {
        setOpen(false);
        if (typeof window !== "undefined") localStorage.setItem(SEEN_KEY, "1");
    };

    const steps = isStaffView ? STAFF_STEPS : LEADER_STEPS;

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-[#3a6b78] hover:underline bg-transparent border-none cursor-pointer p-0"
            >
                <span className="material-symbols-outlined text-[14px]">help</span>
                What is this?
            </button>

            {open && (
                <div onClick={close} data-tour-blocking-modal className="fixed inset-0 z-[100] bg-[rgba(12,34,38,0.5)] flex items-center justify-center p-6">
                    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-[640px] max-w-full max-h-[88vh] overflow-y-auto shadow-2xl">
                        <div className="px-7 py-6 bg-[#004d57] text-white rounded-t-2xl">
                            <div className="text-[11px] tracking-wide font-bold text-[#a9c4bb]">WELCOME TO VOLUNTEERHUB</div>
                            <h2 className="text-xl font-bold mt-1.5">The Gigs Console</h2>
                            <p className="text-[13px] leading-relaxed text-[#cfe0db] mt-2">
                                Gigs are <strong className="text-white">short-term volunteer tasks</strong> — a few hours or a few weeks of specific work, separate from the standing groups and committees people are appointed to.
                            </p>
                        </div>
                        <div className="px-7 py-6">
                            <div className="text-[10.5px] tracking-wide font-bold text-[#88a39b] mb-3">WHAT THE PARTS MEAN</div>
                            <div className="grid grid-cols-2 gap-3.5">
                                {TERMS.map((t) => (
                                    <div key={t.term} className="bg-[#f6f8f7] border border-[#e6ebe9] rounded-lg px-3.5 py-3">
                                        <div className="font-bold text-[13px] text-[#004d57]">{t.term}</div>
                                        <div className="text-xs text-[#3a4d4f] mt-1 leading-relaxed">{t.desc}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="text-[10.5px] tracking-wide font-bold text-[#88a39b] mt-6 mb-2.5">WHAT YOU DO HERE</div>
                            <div className="flex flex-col gap-2">
                                {steps.map((s, i) => (
                                    <div key={i} className="flex gap-2.5 items-start">
                                        <span className="rounded-full bg-[#004d57] text-white text-[11px] font-bold flex items-center justify-center flex-none" style={{ width: 22, height: 22 }}>{i + 1}</span>
                                        <span className="text-[13px] text-[#2c3e40] leading-relaxed">{s}</span>
                                    </div>
                                ))}
                            </div>
                            <p className="text-xs text-[#5a6b6d] leading-relaxed mt-5">
                                The <strong>Access Scope Lens</strong> below switches which slice of the platform you are looking at — your own group, everything you oversee, or the confidential ethics intake if you are authorised for it.
                            </p>
                        </div>
                        <div className="px-7 py-4 bg-[#f4f6f5] border-t border-[#e6ebe9] flex justify-between items-center gap-2.5 rounded-b-2xl">
                            <span className="text-[11.5px] text-[#88a39b]">Reachable any time from &quot;What is this?&quot; in the header.</span>
                            <button
                                onClick={close}
                                className="border-none bg-[#004d57] text-white text-[13px] font-bold rounded-lg px-5 py-2.5 cursor-pointer"
                            >
                                Got it
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
